import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { sharedApi } from "../../../services/sharedApi";
import { useAppSelector } from "../../../store";
import { selectIsAuthenticated } from "../../../store/selectors/authSelectors";
import { loginUser, register } from "../../../store/slices/authSlice";
import { fetchRegistrationSettings } from "../../../store/slices/registrationSlice";
import { isSupportedLanguage } from "../../../utils/languages";
import {
  buildPostAuthCheckoutUrl,
  getCheckoutIntentFromSearchParams,
  loadCheckoutIntent,
  saveCheckoutIntent,
} from "../../payments/utils/checkoutIntent";
import AuthForm from "../components/AuthForm";

import type { AppDispatch } from "../../../store";

function detectBrowserLanguage(): string {
  const lang = navigator.language.split("-")[0];
  return isSupportedLanguage(lang) ? lang : "en";
}

function sanitizeLang(value: string | null): string | null {
  if (!value) return null;
  return isSupportedLanguage(value) ? value : null;
}

const AuthContainer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("auth");
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const registrationEnabled = useAppSelector(
    (state) => state.registration.registrationEnabled,
  );

  useEffect(() => {
    const refresh = (): void => {
      void dispatch(fetchRegistrationSettings());
    };
    refresh();
    window.addEventListener("focus", refresh);
    const interval = window.setInterval(refresh, 30000);
    return (): void => {
      window.removeEventListener("focus", refresh);
      window.clearInterval(interval);
    };
  }, [dispatch]);

  useEffect(() => {
    const checkoutIntent = getCheckoutIntentFromSearchParams(searchParams);
    if (checkoutIntent) saveCheckoutIntent(checkoutIntent);
  }, [searchParams]);

  useEffect(() => {
    sharedApi.auth
      .checkActivePromoCodes()
      .then((res) => setHasActivePromoCodes(res.hasActiveCodes))
      .catch(() => setHasActivePromoCodes(false));
  }, []);

  const wasAuthenticatedOnMount = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticatedOnMount.current) {
      const checkoutIntent = loadCheckoutIntent();
      const from = (location.state as { from?: { pathname?: string } } | null)
        ?.from?.pathname;
      navigate(
        from ||
          (checkoutIntent ? buildPostAuthCheckoutUrl(checkoutIntent) : "/home"),
        { replace: true },
      );
    }
  }, [location.state, navigate]);

  // URL params from landing page (?target=es&native=en)
  const urlTarget = sanitizeLang(searchParams.get("target"));
  const urlNative = sanitizeLang(searchParams.get("native"));
  // ?register=true opens registration tab directly
  const startAsNewUser = searchParams.get("register") === "true" || !!urlTarget;

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nativeLanguage, setNativeLanguage] = useState(
    urlNative ?? detectBrowserLanguage(),
  );
  const [targetLanguage, setTargetLanguage] = useState(urlTarget ?? "es");
  const [password, setPassword] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [hasActivePromoCodes, setHasActivePromoCodes] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewUser, setIsNewUser] = useState(startAsNewUser);
  const [registrationStep, setRegistrationStep] = useState<1 | 2>(1);

  // OAuth error handling
  useEffect(() => {
    const errorParam = searchParams.get("error");
    const provider = searchParams.get("provider");
    const details = searchParams.get("details");

    if (errorParam) {
      let msg = "";
      if (errorParam === "REGISTRATION_DISABLED") {
        msg = t("register.errors.registrationDisabled");
      } else if (errorParam === "oauth_failed") {
        msg = t("oauth.errors.failed", { provider: provider || "OAuth" });
        if (details) msg += ` (${decodeURIComponent(details)})`;
      } else {
        msg = decodeURIComponent(errorParam);
      }
      setError(msg);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [searchParams, t]);

  const redirectAfterLogin = (): void => {
    const checkoutIntent = loadCheckoutIntent();
    const from =
      (location.state as { from?: { pathname?: string } })?.from?.pathname ||
      "/home";
    navigate(checkoutIntent ? buildPostAuthCheckoutUrl(checkoutIntent) : from, {
      replace: true,
    });
  };

  const handleRegister = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!registrationEnabled) {
      setError(t("register.errors.registrationDisabled"));
      return;
    }

    if (registerPassword.length < 6) {
      setError(t("register.errors.passwordTooShort"));
      return;
    }
    if (registerPassword !== registerConfirmPassword) {
      setError(t("register.errors.passwordMismatch"));
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        register({
          email,
          password: registerPassword,
          firstName,
          lastName,
          nativeLanguage,
          targetLanguage,
        }),
      ).unwrap();
      redirectAfterLogin();
    } catch (err: unknown) {
      const msg =
        typeof err === "string" ? err : (err as { message?: string }).message;
      setError(
        msg === "REGISTRATION_DISABLED"
          ? t("register.errors.registrationDisabled")
          : msg || t("register.errors.registrationFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await dispatch(loginUser({ email, password })).unwrap();
      redirectAfterLogin();
    } catch (err: unknown) {
      const loginError = err as { message?: string };
      setError(loginError.message || t("password.incorrectPassword"));
    } finally {
      setLoading(false);
    }
  };

  const handleNewUserToggle = (): void => {
    setIsNewUser(!isNewUser);
    setRegistrationStep(1);
    setError(null);
  };

  return (
    <AuthForm
      email={email}
      firstName={firstName}
      lastName={lastName}
      nativeLanguage={nativeLanguage}
      targetLanguage={targetLanguage}
      isNewUser={isNewUser && registrationEnabled}
      registrationEnabled={registrationEnabled}
      registrationStep={registrationStep}
      promoCode={promoCode}
      hasActivePromoCodes={hasActivePromoCodes}
      password={password}
      registerPassword={registerPassword}
      registerConfirmPassword={registerConfirmPassword}
      loading={loading}
      error={error}
      onEmailChange={setEmail}
      onFirstNameChange={setFirstName}
      onLastNameChange={setLastName}
      onNativeLanguageChange={setNativeLanguage}
      onTargetLanguageChange={setTargetLanguage}
      onNewUserToggle={handleNewUserToggle}
      onPromoCodeChange={setPromoCode}
      onPasswordChange={setPassword}
      onRegisterPasswordChange={setRegisterPassword}
      onRegisterConfirmPasswordChange={setRegisterConfirmPassword}
      onRegister={handleRegister}
      onPasswordLogin={handlePasswordLogin}
      onNextRegistrationStep={() => setRegistrationStep(2)}
      onBackRegistrationStep={() => {
        setRegistrationStep(1);
        setError(null);
      }}
      onErrorDismiss={() => setError(null)}
    />
  );
};

export default AuthContainer;
